import { RelationRole } from "../../contracts/base";
import CultureInfo from "../../culture-info";
import Guard from "../../guard";
import { CultureLoadOption } from "./culture-load-option";
import { EntityLoadConfiguration, IEntityLoadConfiguration } from "./entity-load-configuration";
import { LoadOption } from "./load-options";
import { PropertyLoadOption } from "./property-load-option";
import { RelationLoadOption } from "./relation-load-option";
import { IRelationSpecification, RelationSpecification } from "./relation-specification";

/**
 * An interface for a builder for instances of {@link IEntityLoadConfiguration}.
 */
export interface IEntityLoadConfigurationBuilder {
    /**
     * Indicates that entities should be loaded in the cultures specified by the load option.
     * @param loadOption - The load option
     */
    inCultures(loadOption: LoadOption): IEntityLoadConfigurationBuilder;

    /**
     * Indicates that entities should be loaded in the specified cultures.
     * @param culture - The cultures
     */
    inCultures(cultures: Array<CultureInfo>): IEntityLoadConfigurationBuilder;

    /**
     * Indicates that entities should be loaded in the specified culture.
     * @param culture - The culture
     */
    inCulture(culture: CultureInfo): IEntityLoadConfigurationBuilder;

    /**
     * Indicates that entities should be loaded with the properties specified by the load option.
     * @param loadOption - The load option
     */
    withProperties(loadOption: LoadOption): IEntityLoadConfigurationBuilder;

    /**
     * Indicates that entities should be loaded with the specified properties.
     * @param properties - The properties
     */
    withProperties(properties: Array<string>): IEntityLoadConfigurationBuilder;

    /**
     * Indicates that entities should be loaded with the specified property.
     * @param property - The name of the property
     */
    withProperty(property: string): IEntityLoadConfigurationBuilder;

    /**
     * Indicates that entities should be loaded with the relations specified by the load option.
     * @param loadOption - The load option
     */
    withRelations(loadOption: LoadOption): IEntityLoadConfigurationBuilder;

    /**
     * Indicates that entities should be loaded with the specified relations.
     * @param relations - The relations
     */
    withRelations(relations: Array<string>): IEntityLoadConfigurationBuilder;

    /**
     * Indicates that entities should be loaded with the specified relation specifications.
     * @param specifications - The relation specifications
     */
    withRelations(specifications: Array<IRelationSpecification>): IEntityLoadConfigurationBuilder;

    /**
     * Indicates that entities should be loaded with the specified relation in the specified role.
     * @param relation - The name of the relation
     * @param role - The role of the relation
     */
    withRelation(relation: string, role?: RelationRole): IEntityLoadConfigurationBuilder;

    /**
     * Gets the resulting instance of {@link IEntityLoadConfiguration}.
     */
    build(): IEntityLoadConfiguration;
}

export class EntityLoadConfigurationBuilder implements IEntityLoadConfigurationBuilder {
    private readonly _entityLoadConfiguration: IEntityLoadConfiguration = new EntityLoadConfiguration();

    constructor(entityLoadConfiguration?: IEntityLoadConfiguration) {
        if (entityLoadConfiguration) this._entityLoadConfiguration = entityLoadConfiguration.deepClone();
    }

    inCultures(option: LoadOption | Array<CultureInfo>): IEntityLoadConfigurationBuilder {
        Guard.notNullOrUndefined(option);

        if (option instanceof Array) {
            this._entityLoadConfiguration.cultureLoadOption =
                this._entityLoadConfiguration.cultureLoadOption || new CultureLoadOption();

            this._entityLoadConfiguration.cultureLoadOption.loadOption = LoadOption.Custom;
            this._entityLoadConfiguration.cultureLoadOption.cultures = [
                ...new Set((this._entityLoadConfiguration.cultureLoadOption.cultures || []).concat(option)),
            ];
        } else {
            this._entityLoadConfiguration.cultureLoadOption =
                this._entityLoadConfiguration.cultureLoadOption || new CultureLoadOption();
            this._entityLoadConfiguration.cultureLoadOption.loadOption = option;
        }

        return this;
    }
    inCulture(culture: CultureInfo): IEntityLoadConfigurationBuilder {
        Guard.notUndefined(culture);
        Guard.stringNotNullOrEmpty(culture);

        return this.inCultures([culture]);
    }

    withProperties(option: LoadOption | Array<string>): IEntityLoadConfigurationBuilder {
        Guard.notNullOrUndefined(option);

        if (option instanceof Array) {
            this._entityLoadConfiguration.propertyLoadOption =
                this._entityLoadConfiguration.propertyLoadOption || new PropertyLoadOption();

            this._entityLoadConfiguration.propertyLoadOption.loadOption = LoadOption.Custom;
            this._entityLoadConfiguration.propertyLoadOption.properties = [
                ...new Set((this._entityLoadConfiguration.propertyLoadOption.properties || []).concat(option)),
            ];
        } else {
            this._entityLoadConfiguration.propertyLoadOption =
                this._entityLoadConfiguration.propertyLoadOption || new PropertyLoadOption();
            this._entityLoadConfiguration.propertyLoadOption.loadOption = option;
        }

        return this;
    }

    withProperty(property: string): IEntityLoadConfigurationBuilder {
        Guard.notUndefined(property);
        Guard.stringNotNullOrEmpty(property);

        return this.withProperties([property]);
    }

    withRelations(option: LoadOption | Array<string | IRelationSpecification>): IEntityLoadConfigurationBuilder {
        Guard.notNullOrUndefined(option);

        if (option instanceof Array) {
            let relationSpecifications = option.map(relation => {
                return typeof relation === "string" ? new RelationSpecification(relation) : relation;
            });

            this._entityLoadConfiguration.relationLoadOption =
                this._entityLoadConfiguration.relationLoadOption || new RelationLoadOption();

            this._entityLoadConfiguration.relationLoadOption.loadOption = LoadOption.Custom;

            relationSpecifications = relationSpecifications.filter(value => {
                return (
                    this._entityLoadConfiguration.relationLoadOption.relations.findIndex(relationValue => {
                        if (typeof value === "string") {
                            return relationValue.name === value;
                        } else {
                            return relationValue.deepEquals(value);
                        }
                    }) === -1
                );
            });

            this._entityLoadConfiguration.relationLoadOption.relations = (
                this._entityLoadConfiguration.relationLoadOption.relations || []
            ).concat(relationSpecifications);
        } else {
            this._entityLoadConfiguration.relationLoadOption =
                this._entityLoadConfiguration.relationLoadOption || new RelationLoadOption();
            this._entityLoadConfiguration.relationLoadOption.loadOption = option;
        }

        return this;
    }

    withRelation(relation: string, role?: RelationRole): IEntityLoadConfigurationBuilder {
        Guard.notUndefined(relation);
        Guard.stringNotNullOrEmpty(relation);

        return this.withRelations([new RelationSpecification(relation, role)]);
    }

    build(): Readonly<IEntityLoadConfiguration> {
        return this._entityLoadConfiguration.deepClone();
    }
}
